// Клиентское окружение экранов выгрузки Claude Design.
// FFTheme — тот же интерфейс, что ff-theme.js, но тема хранится в общем
// механизме портала (localStorage sst-theme + html[data-theme]); тёмные
// переменные задаёт dc-base.css. FFMotion — дословный перенос ff-motion.js.
import { applyTheme } from '@/composables/useTheme';

type Theme = 'light' | 'dark';

export function installTheme() {
  const w = window as unknown as { FFTheme?: unknown };
  if (w.FFTheme) return;
  const current = (): Theme => (document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light');
  const apply = (value: Theme) => {
    applyTheme(value);
    document.documentElement.setAttribute('data-ff-theme', value);
    document.documentElement.style.colorScheme = value;
  };
  // При запуске только отражаем текущую тему, не сохраняя её как выбор: иначе миниаппа
  // решила бы, что человек уже выбрал светлую, и не унаследовала бы схему Telegram.
  document.documentElement.setAttribute('data-ff-theme', current());
  document.documentElement.style.colorScheme = current();
  const set = (value: Theme) => { apply(value); window.dispatchEvent(new CustomEvent('ff-theme', { detail: value })); };
  w.FFTheme = { get: current, set, toggle: () => set(current() === 'dark' ? 'light' : 'dark') };
}

// ---- ff-motion.js (без изменений логики) ----
export function installMotion() {
  const w = window as unknown as { FFMotion?: unknown };
  if (w.FFMotion) return;
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const seen = new WeakSet<Element>();
  const ease = 'cubic-bezier(.2,.7,.2,1)';
  let queue: Element[] = [], flushT: ReturnType<typeof setTimeout> | null = null;

  function fmt(n: number) { return Math.round(n).toLocaleString('ru-RU'); }

  const counting = new WeakMap<Element, object>();
  function countUp(el: Element) {
    const target = parseFloat(el.getAttribute('data-count') ?? '');
    const node = el.firstChild;
    if (isNaN(target) || !node || node.nodeType !== 3) return;
    const final = node.nodeValue, t0 = performance.now(), dur = 1100, id = {};
    let last: string | null = null;
    counting.set(el, id);
    function step(t: number) {
      if (counting.get(el) !== id) return;
      if (last !== null && node!.nodeValue !== last) return;
      const p = Math.min(1, (t - t0) / dur), e = 1 - Math.pow(1 - p, 3);
      last = p < 1 ? fmt(target * e) : final;
      node!.nodeValue = last;
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function play(el: HTMLElement, i: number) {
    const d = (parseFloat(el.getAttribute('data-delay') ?? '') || 0) + i * 70;
    if (el.hasAttribute('data-reveal')) {
      el.animate([{ opacity: 0, transform: 'translateY(18px)' }, { opacity: 1, transform: 'none' }],
        { duration: 700, delay: d, easing: ease, fill: 'backwards' });
    }
    if (el.hasAttribute('data-pop')) {
      const r = el.getAttribute('data-pop') || '0';
      el.animate([{ opacity: 0, transform: 'scale(.6) rotate(' + (parseFloat(r) - 12) + 'deg)' }, { opacity: 1, transform: 'scale(1) rotate(' + r + 'deg)' }],
        { duration: 650, delay: d, easing: 'cubic-bezier(.3,1.4,.5,1)', fill: 'backwards' });
    }
    if (el.hasAttribute('data-grow')) {
      el.style.transformOrigin = 'left center';
      el.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], { duration: 1100, delay: d + 150, easing: ease, fill: 'backwards' });
    }
    if (el.hasAttribute('data-count')) setTimeout(() => countUp(el), d);
    if (el.hasAttribute('data-donut')) {
      const full = el.getAttribute('stroke-dasharray');
      if (full) el.animate([{ strokeDasharray: '0 1000' }, { strokeDasharray: full }], { duration: 1200, delay: d + 200, easing: ease, fill: 'backwards' });
    }
  }

  const io = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(en => {
      if (!en.isIntersecting) return;
      io!.unobserve(en.target);
      queue.push(en.target);
    });
    if (!flushT) flushT = setTimeout(() => {
      flushT = null;
      queue.forEach((el, i) => play(el as HTMLElement, i));
      queue = [];
    }, 16);
  }, { rootMargin: '0px 0px -6% 0px', threshold: 0.05 }) : null;

  function loops(el: HTMLElement) {
    const ph = Math.random() * 2000;
    if (el.hasAttribute('data-twinkle')) {
      el.animate([{ transform: 'scale(1) rotate(0deg)', opacity: 1 }, { transform: 'scale(.72) rotate(18deg)', opacity: 0.55 }, { transform: 'scale(1) rotate(0deg)', opacity: 1 }],
        { duration: 2400 + Math.random() * 900, delay: -ph, iterations: Infinity, easing: 'ease-in-out' });
    }
    if (el.hasAttribute('data-spin')) {
      const dir = el.getAttribute('data-spin') === 'rev' ? -360 : 360;
      el.animate([{ transform: 'rotate(0deg)' }, { transform: 'rotate(' + dir + 'deg)' }], { duration: 48000, iterations: Infinity });
    }
    if (el.hasAttribute('data-float')) {
      const r = parseFloat(el.getAttribute('data-float') ?? '') || 0;
      el.animate([{ transform: 'translateY(0) rotate(' + r + 'deg)' }, { transform: 'translateY(-6px) rotate(' + (r + 1.5) + 'deg)' }, { transform: 'translateY(0) rotate(' + r + 'deg)' }],
        { duration: 4200 + Math.random() * 1200, delay: -ph, iterations: Infinity, easing: 'ease-in-out' });
    }
    if (el.hasAttribute('data-drift')) {
      el.animate([{ transform: 'translateX(0)' }, { transform: 'translateX(-28px)' }, { transform: 'translateX(0)' }], { duration: 16000, iterations: Infinity, easing: 'ease-in-out' });
    }
    if (el.hasAttribute('data-wiggle')) {
      el.addEventListener('mouseenter', () => {
        el.animate([{ transform: 'rotate(0)' }, { transform: 'rotate(-8deg)' }, { transform: 'rotate(6deg)' }, { transform: 'rotate(0)' }], { duration: 500, easing: 'ease-out' });
      });
    }
  }

  function scan(root?: ParentNode) {
    if (reduce) return;
    (root || document).querySelectorAll<HTMLElement>('[data-drop],[data-reveal],[data-pop],[data-grow],[data-count],[data-donut],[data-twinkle],[data-spin],[data-float],[data-drift],[data-wiggle]').forEach(el => {
      if (seen.has(el)) return;
      seen.add(el);
      if (el.hasAttribute('data-drop')) {
        el.animate([{ opacity: 0, transform: 'translateY(-8px) scale(.98)' }, { opacity: 1, transform: 'none' }], { duration: 220, easing: ease });
        return;
      }
      loops(el);
      if (el.matches('[data-reveal],[data-pop],[data-grow],[data-count],[data-donut]')) {
        if (io) io.observe(el); else play(el, 0);
      }
    });
  }

  let mt: ReturnType<typeof setTimeout> | null = null;
  function schedule() { if (mt) return; mt = setTimeout(() => { mt = null; scan(); }, 60); }
  function start() {
    scan();
    new MutationObserver(muts => {
      muts.forEach(m => {
        if (m.type === 'attributes' && m.attributeName === 'data-count' && seen.has(m.target as Element) && !reduce) setTimeout(() => countUp(m.target as Element), 30);
      });
      schedule();
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-count'] });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();

  w.FFMotion = { scan };
}

/** Внутренние ссылки экранов (<a href="/...">) открываются без перезагрузки страницы. */
export function installLinks(go: (path: string) => unknown) {
  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (!link || !link.closest('.sc-host') || link.target === '_blank' || link.hasAttribute('download')) return;
    const href = link.getAttribute('href') ?? '';
    if (href === '#' || href.startsWith('#')) { event.preventDefault(); return; }
    if (!href.startsWith('/') || href.startsWith('//')) return;
    event.preventDefault();
    void go(href);
  });
}

/**
 * data-dc-fit: заголовок с настоящими данными (например, длинное имя) уменьшает шрифт ровно настолько,
 * чтобы самое длинное слово поместилось в ширину блока. Если текст помещается — вёрстка как в макете.
 */
export function installFit() {
  const fit = () => {
    document.querySelectorAll<HTMLElement>('[data-dc-fit]').forEach(el => {
      el.style.fontSize = '';
      const width = el.clientWidth;
      if (!width || el.scrollWidth <= width + 1) return;
      const base = parseFloat(getComputedStyle(el).fontSize);
      el.style.fontSize = `${Math.floor(base * width / el.scrollWidth)}px`;
    });
  };
  let queued = false;
  const schedule = () => { if (queued) return; queued = true; requestAnimationFrame(() => { queued = false; fit(); }); };
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true, characterData: true });
  window.addEventListener('resize', schedule);
  document.fonts?.ready.then(schedule);
  schedule();
}
